import type {
  AssetPoolFilters,
  CrewPoolFilters,
  PlannerActivityListFilters,
  SchedulingGridFilters,
  UnplannedWorkFilters,
} from '../types';

/**
 * Query key factory for the Planner feature (Wave 8).
 *
 * Always derive keys from this factory rather than hand-writing arrays so
 * mutation invalidation stays consistent across the home, scheduling grid,
 * execution review, crew (Agent B) and insights (Agent C) surfaces.
 *
 * Layout:
 *   ['planner']
 *   ['planner', 'activities']
 *   ['planner', 'activities', 'list', { from, to }]
 *   ['planner', 'activities', 'detail']
 *   ['planner', 'activities', 'detail', id]
 *   ['planner', 'scheduling-grid', { from, to, mode, supervisorUserId }]
 *   ['planner', 'unplanned', { search }]
 *   ['planner', 'asset-pool', { date, assetClass }]
 *   ['planner', 'crew-pool', { date }]
 *   ['planner', 'execution-lines', activityId]
 *   ['planner', 'available-tasks', activityId]
 *
 *   Agent B adds:
 *   ['planner', 'crew-assignments', { ... }]
 *   ['planner', 'crew-availability', { ... }]
 *
 *   Agent C adds:
 *   ['planner', 'insights', { ... }]
 *   ['planner', 'site-workload', { date }]
 */
export const plannerKeys = {
  all: ['planner'] as const,

  /* Activities — the scheduling grid + execution review */
  activities: () => [...plannerKeys.all, 'activities'] as const,
  activityLists: () => [...plannerKeys.activities(), 'list'] as const,
  activityList: (filters: PlannerActivityListFilters) =>
    [...plannerKeys.activityLists(), filters] as const,
  activityDetails: () => [...plannerKeys.activities(), 'detail'] as const,
  activityDetail: (id: string) => [...plannerKeys.activityDetails(), id] as const,

  /* Scheduling grid — single cache entry per range / mode combo */
  schedulingGrids: () => [...plannerKeys.all, 'scheduling-grid'] as const,
  schedulingGrid: (filters: SchedulingGridFilters) =>
    [...plannerKeys.schedulingGrids(), filters] as const,

  /* Unplanned work — left-rail "to schedule" queue */
  unplanned: () => [...plannerKeys.all, 'unplanned'] as const,
  unplannedList: (filters: UnplannedWorkFilters) =>
    [...plannerKeys.unplanned(), filters] as const,

  /* Resource pools — surface on the scheduling grid sidebars */
  assetPools: () => [...plannerKeys.all, 'asset-pool'] as const,
  assetPool: (filters: AssetPoolFilters) =>
    [...plannerKeys.assetPools(), filters] as const,
  crewPools: () => [...plannerKeys.all, 'crew-pool'] as const,
  crewPool: (filters: CrewPoolFilters) =>
    [...plannerKeys.crewPools(), filters] as const,

  /* Execution-line surface — the Execution page detail */
  executionLines: (activityId: string) =>
    [...plannerKeys.all, 'execution-lines', activityId] as const,
  availableTasks: (activityId: string) =>
    [...plannerKeys.all, 'available-tasks', activityId] as const,

  /* Agent B reservations — crew assignments + availability */
  crewAssignments: () => [...plannerKeys.all, 'crew-assignments'] as const,
  crewAvailability: () => [...plannerKeys.all, 'crew-availability'] as const,

  /* Agent C reservations — operational insights */
  insights: () => [...plannerKeys.all, 'insights'] as const,
  siteWorkload: () => [...plannerKeys.all, 'site-workload'] as const,
};
